<script setup lang="ts">
import { __ } from '@@/bits/intl'
import { getEditorObject } from '@@/bits/tldraw/editor'
import { isEditorIdle } from '@@/bits/whiteboard_preview_utils'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardFramePreviewStore } from '@@/pinia/whiteboard_frame_preview'
import { useWhiteboardObjectPreviewStore } from '@@/pinia/whiteboard_object_preview'
import WhiteboardFramePreviewOld from '@@/vuecomponents/WhiteboardFramePreviewOld.vue'
import { storeToRefs } from 'pinia'

import { react, type TLPageId, type TLShapeId } from 'tldraw'
import { computed, onMounted } from 'vue'

const surfaceStore = useSurfaceStore()
const whiteboardStore = useWhiteboardStore()
const whiteboardFramePreviewStore = useWhiteboardFramePreviewStore()
const whiteboardObjectPreviewStore = useWhiteboardObjectPreviewStore()
const { currentFrameId } = storeToRefs(whiteboardStore)

defineProps<{
  selectedShapeIdForScript?: string | null
}>()

const emit = defineEmits<{
  (e: 'switch-frame', frameId: TLPageId): void
}>()

const switchFrame = (frameId: TLPageId) => {
  emit('switch-frame', frameId)
}

function selectShape(objectId: TLShapeId) {
  const editor = getEditorObject()
  if (editor == null) {
    return
  }
  const shape = editor.getShape(objectId)
  if (shape == null) {
    return
  }

  const pageId = editor.getAncestorPageId(shape)
  if (pageId == null) {
    return
  }
  switchFrame(pageId)
  editor.select(shape)
}

const objectsWithPreviewsByFrameInOrder = computed(() => {
  const editor = getEditorObject()
  if (editor == null) {
    return []
  }
  return whiteboardStore.frames
    .map((frame, index) => {
      return {
        frameIndex: index,
        frameId: frame.id,
        objects: whiteboardObjectPreviewStore.objectIdsWithPreviews.filter(
          (objectId: TLShapeId) => editor.getAncestorPageId(objectId) === frame.id,
        ),
      }
    })
    .filter((objectsInFrame) => objectsInFrame.objects.length > 0)
})

onMounted(() => {
  const editor = getEditorObject()
  if (editor == null) {
    return
  }
  editor.store.listen(whiteboardObjectPreviewStore.debouncedHandlePageChange, { scope: 'document', source: 'all' })
  // Update frames with changes when editor.getPath() changes
  react('update pending object previews', () => {
    if (!isEditorIdle()) {
      return
    }
    if (whiteboardObjectPreviewStore.objectIdsWithPreviews.length === 0) {
      return
    }
    whiteboardObjectPreviewStore.conditionallyUpdateObjectPreviews()
  })
})
</script>

<template>
  <div class="flex flex-col">
    <!-- Frame picker for code mode -->
    <div
      :class="[
        'flex flex-wrap gap-4 mt-3 w-full',
        'rounded-2xl',
        'box-border',
        'p-1',
        {
          'bg-popover-dark': surfaceStore.backgroundLuminance === 'dark',
          'bg-popover-light': surfaceStore.backgroundLuminance === 'light',
          'backdrop-blur-10': true,
        },
      ]"
    >
      <div
        :class="[
          'p-2',
          'flex flex-wrap gap-3 w-full',
          'max-h-[92px]',
          'scrollbar-photo-thin-dark-on-hover',
          'overflow-y-auto',
        ]"
      >
        <WhiteboardFramePreviewOld
          v-for="(frame, index) in whiteboardStore.frames"
          :key="frame.id"
          :frame-id="frame.id"
          :frame-index="index + 1"
          :current-frame-id="currentFrameId"
          :color-scheme="surfaceStore.backgroundLuminance"
          :is-loading-portrait-mobile-layout="false"
          :preview="whiteboardFramePreviewStore.framePreviews[frame.id]"
          :is-draggable="false"
          :hide-context-menu-button="true"
          @switch-frame="switchFrame"
        />
      </div>
    </div>

    <!-- Object picker for code mode -->
    <div
      :class="[
        'mt-3 h-full grow',
        'rounded-2xl',
        'box-border',
        'p-3',
        {
          'bg-popover-light': true,
          'backdrop-blur-10': true,
        },
      ]"
    >
      <div class="flex flex-col gap-4">
        <div
          v-for="objectsInFrame in objectsWithPreviewsByFrameInOrder"
          :key="'code-frame-' + objectsInFrame.frameIndex"
          :class="['flex flex-col gap-2']"
        >
          <OzBaseButton
            :class="[
              'flex',
              'rounded-lg',
              'w-fit',
              'font-semibold',
              'text-dark-text-100',
              'bg-transparent',
              'focus-visible:ring-2',
              'focus-visible:ring-offset-1',
              'focus-visible:ring-offset-popover-light',
              'focus-visible:ring-grape-500',
            ]"
            tabindex="0"
            :aria-label="__('Switch to card %{cardNumber}', { cardNumber: objectsInFrame.frameIndex + 1 })"
            @click="switchFrame(objectsInFrame.frameId)"
            @keydown.enter="switchFrame(objectsInFrame.frameId)"
          >
            {{ __('Card %{cardNumber}', { cardNumber: objectsInFrame.frameIndex + 1 }) }}
          </OzBaseButton>
          <div class="flex flex-wrap gap-4">
            <OzBaseButton
              v-for="objectId in objectsInFrame.objects"
              :key="objectId"
              :class="[
                'w-13',
                'h-13',
                'p-0',
                'rounded-lg',
                'overflow-hidden',
                'bg-transparent',
                'hhover:bg-button-hover-light',
                'hactive:bg-grape-500',
                'focus-visible:ring-2',
                'focus-visible:ring-offset-1',
                'focus-visible:ring-offset-popover-light',
                'focus-visible:ring-grape-500',
                'relative',
                {
                  'ring-2 ring-grape-500 ring-offset-1 ring-offset-popover-light focus-visible:bg-button-hover-light':
                    selectedShapeIdForScript === objectId,
                },
              ]"
              @click="selectShape(objectId)"
              @keydown.enter="selectShape(objectId)"
            >
              <div :class="['w-full', 'h-full']" v-html="whiteboardObjectPreviewStore.objectsPreviews[objectId]"></div>
              <!-- Checkmark if selected -->
              <div
                v-if="selectedShapeIdForScript === objectId"
                class="absolute bottom-0.5 start-0.5 rounded-lg bg-grape-500 text-light-text-100 z-20 w-5 h-5 flex justify-center items-center"
              >
                <OzIcon name="checkmark_thick" :size="16" />
              </div>
            </OzBaseButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
